import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  DocumentData,
  FirestoreDataConverter,
  QueryDocumentSnapshot,
  setDoc,
  SnapshotOptions,
  WithFieldValue,
} from 'firebase/firestore';
import { firestore } from '@/lib/firebase';
import {
  DEFAULT_SINGLE_EVENT_KEY,
  TABLET_COLLECTION,
  Tablet,
} from '../schema/tablet';
import {
  useCollectionData,
  useDocumentData,
} from 'react-firebase-hooks/firestore';

// create a tablet
export async function createTablet(tablet: Tablet) {
  const name = tablet.name.trim();
  await addDoc(collection(firestore, TABLET_COLLECTION), {
    name,
    nameLower: name.toLowerCase(),
    location: tablet.location.trim(),
    groupId: tablet.groupId ?? null,
    suiteId: tablet.suiteId ?? null,
    singleEventKey: tablet.singleEventKey?.trim() || DEFAULT_SINGLE_EVENT_KEY,
    pin: tablet.pin?.trim() || null,
    qrEnabled: tablet.qrEnabled ?? true,
  });
}

// update a tablet
export async function updateTablet(id: string, tablet: Partial<Tablet>) {
  delete tablet.id;

  const update: Partial<Tablet> = { ...tablet };
  if (typeof update.name === 'string') {
    update.name = update.name.trim();
    update.nameLower = update.name.toLowerCase();
  }
  if (typeof update.location === 'string') {
    update.location = update.location.trim();
  }
  if (typeof update.singleEventKey === 'string') {
    update.singleEventKey =
      update.singleEventKey.trim() || DEFAULT_SINGLE_EVENT_KEY;
  }
  if (typeof update.pin === 'string') {
    update.pin = update.pin.trim() || null;
  }

  await setDoc(doc(firestore, TABLET_COLLECTION, id), update, {
    merge: true,
  });
}

// hard delete a tablet
export async function deleteTablet(id: string) {
  await deleteDoc(doc(firestore, TABLET_COLLECTION, id));
}

const tabletConverter: FirestoreDataConverter<Tablet> = {
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options: SnapshotOptions
  ): Tablet {
    const data = snapshot.data(options);
    return {
      suiteId: null,
      singleEventKey: DEFAULT_SINGLE_EVENT_KEY,
      pin: null,
      qrEnabled: true,
      ...data,
      id: snapshot.id,
    } as Tablet;
  },
  toFirestore: function (): WithFieldValue<DocumentData> {
    throw new Error('Function not implemented.');
  },
};

// list all tablets
export function useTablets() {
  return useCollectionData(
    collection(firestore, TABLET_COLLECTION).withConverter(tabletConverter),
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );
}

export function useTablet(id?: string) {
  // Guard: doc() throws on an undefined segment, which would crash the page.
  return useDocumentData(
    id
      ? doc(firestore, TABLET_COLLECTION, id).withConverter(tabletConverter)
      : undefined,
    {
      snapshotListenOptions: { includeMetadataChanges: false },
    }
  );
}
